import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AreaTag,
  CreateAreaTagData,
  UpdateAreaTagData,
  AreaTagApiResponse,
} from '../types/area-tags';
import {
  createAreaTag,
  updateAreaTag,
  patchAreaTag,
  deleteAreaTag,
  uploadAreaTagPhoto,
} from '../api/area-tags-api';
import { areaTagKeys } from './useAreaTags';

/**
 * Hook for creating area tags with optimistic updates
 * @param layoutId - Layout ID for the area tags
 * @returns Mutation object for creating area tags
 */
export const useCreateAreaTag = (layoutId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAreaTag,
    onMutate: async (newAreaData: CreateAreaTagData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: areaTagKeys.list(layoutId) });

      // Snapshot the previous value
      const previousAreaTags = queryClient.getQueryData(areaTagKeys.list(layoutId));

      // Create optimistic area tag object
      const optimisticAreaTag: AreaTag = {
        id: Date.now(), // Temporary ID
        name: newAreaData.name,
        points: newAreaData.coordinates,
        layout: null,
        images: {},
        layout_id: layoutId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Optimistically update the cache
      queryClient.setQueryData(areaTagKeys.list(layoutId), (old: AreaTag[] | undefined) => {
        return old ? [...old, optimisticAreaTag] : [optimisticAreaTag];
      });

      return { previousAreaTags, optimisticAreaTag };
    },
    onError: (_err, _newAreaData, context) => {
      // Rollback the cache to the previous state
      if (context?.previousAreaTags) {
        queryClient.setQueryData(areaTagKeys.list(layoutId), context.previousAreaTags);
      }
    },
    onSuccess: (data: AreaTagApiResponse, _variables, context) => {
      // Replace the optimistic update with real data
      if (data.status === 'success' && data.area_tag_id && context?.optimisticAreaTag) {
        queryClient.setQueryData(areaTagKeys.list(layoutId), (old: AreaTag[] | undefined) => {
          if (!old) return [];

          return old.map((tag) =>
            tag.id === context.optimisticAreaTag.id
              ? { ...context.optimisticAreaTag, id: data.area_tag_id! }
              : tag
          );
        });
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: areaTagKeys.list(layoutId) });
    },
  });
};

/**
 * Hook for updating area tags with optimistic updates
 * @param layoutId - Layout ID for the area tags
 * @returns Mutation object for updating area tags
 */
export const useUpdateAreaTag = (layoutId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ areaTagId, data }: { areaTagId: number; data: UpdateAreaTagData }) =>
      updateAreaTag(areaTagId, data, layoutId),
    onMutate: async ({ areaTagId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: areaTagKeys.list(layoutId) });
      await queryClient.cancelQueries({ queryKey: areaTagKeys.detail(areaTagId, layoutId) });

      // Snapshot the previous values
      const previousAreaTags = queryClient.getQueryData(areaTagKeys.list(layoutId));
      const previousAreaTag = queryClient.getQueryData(areaTagKeys.detail(areaTagId, layoutId));

      // Optimistically update the list cache
      queryClient.setQueryData(areaTagKeys.list(layoutId), (old: AreaTag[] | undefined) => {
        if (!old) return [];

        return old.map((tag) =>
          tag.id === areaTagId
            ? {
                ...tag,
                ...data,
                updated_at: new Date().toISOString(),
              }
            : tag
        );
      });

      // Optimistically update the detail cache if it exists
      queryClient.setQueryData(
        areaTagKeys.detail(areaTagId, layoutId),
        (old: AreaTag | undefined) => {
          if (!old) return old;

          return {
            ...old,
            ...data,
            updated_at: new Date().toISOString(),
          };
        }
      );

      return { previousAreaTags, previousAreaTag };
    },
    onError: (_err, { areaTagId }, context) => {
      // Rollback the cache to the previous state
      if (context?.previousAreaTags) {
        queryClient.setQueryData(areaTagKeys.list(layoutId), context.previousAreaTags);
      }
      if (context?.previousAreaTag) {
        queryClient.setQueryData(areaTagKeys.detail(areaTagId, layoutId), context.previousAreaTag);
      }
    },
    onSuccess: (data: AreaTagApiResponse, { areaTagId }) => {
      // Update with real data if available
      if (data.status === 'success' && data.data) {
        queryClient.setQueryData(areaTagKeys.detail(areaTagId, layoutId), data.data);

        // Update the list cache
        queryClient.setQueryData(areaTagKeys.list(layoutId), (old: AreaTag[] | undefined) => {
          if (!old) return [];

          return old.map((tag) => (tag.id === areaTagId ? data.data! : tag));
        });
      }
    },
    onSettled: (_data, _error, { areaTagId }) => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: areaTagKeys.list(layoutId) });
      queryClient.invalidateQueries({ queryKey: areaTagKeys.detail(areaTagId, layoutId) });
    },
  });
};

/**
 * Hook for patching area tags (partial updates) with optimistic updates
 * @param layoutId - Layout ID for the area tags
 * @param projectId - Project ID required for API calls
 * @returns Mutation object for patching area tags
 */
export const usePatchAreaTag = (layoutId: string, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ areaTagId, data }: { areaTagId: number; data: Partial<UpdateAreaTagData> }) => {
      return patchAreaTag(areaTagId, data, layoutId, projectId);
    },

    onMutate: async ({ areaTagId, data }) => {
      await queryClient.cancelQueries({ queryKey: areaTagKeys.list(layoutId) });
      await queryClient.cancelQueries({ queryKey: areaTagKeys.detail(areaTagId, layoutId) });

      const previousAreaTags = queryClient.getQueryData<AreaTag[]>(areaTagKeys.list(layoutId));
      const previousAreaTag = queryClient.getQueryData<AreaTag>(areaTagKeys.detail(areaTagId, layoutId));

      const updateData = {
        ...data,
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueryData<AreaTag[]>(areaTagKeys.list(layoutId), (old) => {
        if (!old) return [];
        return old.map((tag) => (tag.id === areaTagId ? { ...tag, ...updateData } : tag));
      });

      queryClient.setQueryData<AreaTag>(areaTagKeys.detail(areaTagId, layoutId), (old) => {
        if (!old) return old;
        return { ...old, ...updateData };
      });

      return { previousAreaTags, previousAreaTag };
    },

    onError: (_err, { areaTagId }, context) => {
      if (context?.previousAreaTags) {
        queryClient.setQueryData(areaTagKeys.list(layoutId), context.previousAreaTags);
      }
      if (context?.previousAreaTag) {
        queryClient.setQueryData(areaTagKeys.detail(areaTagId, layoutId), context.previousAreaTag);
      }
    },

    onSuccess: (data: AreaTagApiResponse, { areaTagId }) => {
      if (data.status === 'success' && data.data) {
        queryClient.setQueryData(areaTagKeys.detail(areaTagId, layoutId), data.data);

        queryClient.setQueryData<AreaTag[]>(areaTagKeys.list(layoutId), (old) => {
          if (!old) return [];
          return old.map((tag) => (tag.id === areaTagId ? data.data! : tag));
        });
      }
    },

    onSettled: (_data, _error, { areaTagId }) => {
      queryClient.invalidateQueries({ queryKey: areaTagKeys.list(layoutId) });
      queryClient.invalidateQueries({ queryKey: areaTagKeys.detail(areaTagId, layoutId) });
    },
  });
};

/**
 * Hook for deleting area tags with optimistic updates
 * @param layoutId - Layout ID for the area tags
 * @param projectId - Project ID required for API call
 * @returns Mutation object for deleting area tags
 */
export const useDeleteAreaTag = (layoutId: string, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (areaTagId: number) => deleteAreaTag(areaTagId, layoutId, projectId),
    onMutate: async (areaTagId: number) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: areaTagKeys.list(layoutId) });
      await queryClient.cancelQueries({ queryKey: areaTagKeys.detail(areaTagId, layoutId) });

      // Snapshot the previous values
      const previousAreaTags = queryClient.getQueryData(areaTagKeys.list(layoutId));
      const previousAreaTag = queryClient.getQueryData(areaTagKeys.detail(areaTagId, layoutId));

      // Optimistically remove the area tag
      queryClient.setQueryData(areaTagKeys.list(layoutId), (old: AreaTag[] | undefined) => {
        if (!old) return [];
        return old.filter((tag) => tag.id !== areaTagId);
      });

      // Remove from detail cache
      queryClient.removeQueries({ queryKey: areaTagKeys.detail(areaTagId, layoutId) });

      return { previousAreaTags, previousAreaTag, areaTagId };
    },
    onError: (_err, areaTagId, context) => {
      // Rollback the cache to the previous state
      if (context?.previousAreaTags) {
        queryClient.setQueryData(areaTagKeys.list(layoutId), context.previousAreaTags);
      }
      if (context?.previousAreaTag) {
        queryClient.setQueryData(areaTagKeys.detail(areaTagId, layoutId), context.previousAreaTag);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: areaTagKeys.list(layoutId) });
    },
  });
};

/**
 * Hook for uploading area tag photos
 * @param layoutId - Layout ID for the area tags
 * @returns Mutation object for uploading photos
 */
export const useUploadAreaTagPhoto = (layoutId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ areaTagId, photo }: { areaTagId: number; photo: File }) =>
      uploadAreaTagPhoto(areaTagId, photo, layoutId),
    onSuccess: (data: AreaTagApiResponse, { areaTagId }) => {
      // Update cache with new photo data if available
      if (data.status === 'success' && data.data) {
        queryClient.setQueryData(areaTagKeys.detail(areaTagId, layoutId), data.data);

        queryClient.setQueryData(areaTagKeys.list(layoutId), (old: AreaTag[] | undefined) => {
          if (!old) return [];
          return old.map((tag) => (tag.id === areaTagId ? data.data! : tag));
        });
      }
    },
    onSettled: (_data, _error, { areaTagId }) => {
      // Refetch to ensure we have the latest photo data
      queryClient.invalidateQueries({ queryKey: areaTagKeys.detail(areaTagId, layoutId) });
      queryClient.invalidateQueries({ queryKey: areaTagKeys.list(layoutId) });
    },
  });
};

/**
 * Combined mutations hook for easier consumption
 * @param layoutId - Layout ID for the area tags
 * @param projectId - Project ID required for API calls
 * @returns Object with all mutation hooks
 */
export const useAreaTagMutations = (layoutId: string, projectId: string) => {
  return {
    createAreaTag: useCreateAreaTag(layoutId),
    updateAreaTag: useUpdateAreaTag(layoutId),
    patchAreaTag: usePatchAreaTag(layoutId, projectId),
    deleteAreaTag: useDeleteAreaTag(layoutId, projectId),
    uploadPhoto: useUploadAreaTagPhoto(layoutId),
  };
};
